import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { onAuthStateChanged } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { useHistory } from "react-router-dom";
import Navbar from "../Components/Navbar/navbar";
import HistoricoCadastro from "../Components/HistoricoCadastro/HistoricoCadastro";
import { auth, db } from "../Config/firebase";
import { contemTextoPesquisa } from "../Utils/pesquisaTexto";
import { gerarSequencial, obterMaiorSequencial } from "../Utils/sequencial";
import { aplicarTenantAtual, consultaTenantAtual } from "../Utils/tenant";
import useEstadoSalvamento from "../Utils/useEstadoSalvamento";
import { criarArea } from "./esclerometriaCalculos";
import { atualizarDocumentoComMesclagem } from "../Utils/concorrenciaFirestore";

const estadoInicial = {
  codigo: "",
  dataRelatorio: new Date().toISOString().split("T")[0],
  dataEnsaio: "",
  cliente: "",
  clienteId: "",
  clienteDocumento: "",
  obra: "",
  obraId: "",
  local: "",
  endereco: "",
  quantidadeAreas: 1,
  areas: [],
  norma: "ABNT NBR 7584:2026",
};

function formatarDataBR(dataISO) {
  if (!dataISO) return "-";
  const [ano, mes, dia] = String(dataISO).split("-");
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : dataISO;
}

function normalizar(valor) {
  return String(valor || "").toLowerCase();
}

function obraAtiva(obra = {}) {
  const status = normalizar(obra.status || obra.Status);
  return obra.ativo !== false && !["inativo", "inativa", "cancelado", "cancelada"].includes(status);
}

export default function CadastroEsclerometria() {
  const history = useHistory();
  const { salvandoCadastro, executarSalvamento } = useEstadoSalvamento();
  const [usuario, setUsuario] = useState(null);
  const [ensaios, setEnsaios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [obras, setObras] = useState([]);
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [form, setForm] = useState(estadoInicial);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [itensPorPagina, setItensPorPagina] = useState(20);

  useEffect(() => onAuthStateChanged(auth, async (user) => {
    if (!user) return setUsuario(null);
    const snap = await getDoc(doc(db, "users", user.uid));
    setUsuario(snap.exists() ? { uid: user.uid, ...snap.data() } : { uid: user.uid });
  }), []);

  useEffect(() => {
    carregarEnsaios();
    carregarClientes();
  }, []);

  async function carregarEnsaios() {
    setLoading(true);
    try {
      const consulta = query(collection(db, "tblEsclerometrias"), orderBy("criadoEm", "desc"));
      const snap = await getDocs(consultaTenantAtual(consulta));
      setEnsaios(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
    } catch (error) {
      console.error("Erro ao carregar ensaios de esclerometria:", error);
      setEnsaios([]);
    } finally {
      setLoading(false);
    }
  }

  async function carregarClientes() {
    const snap = await getDocs(consultaTenantAtual(query(collection(db, "clientes"), orderBy("nome", "asc"))));
    setClientes(snap.docs.map((item) => ({ id: item.id, ...item.data() })));
  }

  async function carregarObras(clienteId) {
    if (!clienteId) return setObras([]);
    const consulta = query(collection(db, "obras"), where("clienteId", "==", clienteId), orderBy("nome", "asc"));
    const snap = await getDocs(consultaTenantAtual(consulta));
    setObras(snap.docs.map((item) => ({ id: item.id, ...item.data() })).filter(obraAtiva));
  }

  function abrirNovo() {
    setEditando(null);
    setClienteSelecionado(null);
    setObras([]);
    setForm({ ...estadoInicial });
    setOpen(true);
  }

  async function abrirEdicao(item) {
    setEditando(item);
    const cliente = clientes.find((opcao) => opcao.id === item.clienteId) || null;
    setClienteSelecionado(cliente);
    await carregarObras(item.clienteId);
    setForm({ ...estadoInicial, ...item, quantidadeAreas: item.quantidadeAreas || item.areas?.length || 1 });
    setOpen(true);
  }

  async function salvar() {
    if (!form.clienteId || !form.obraId || !form.dataEnsaio) {
      alert("Selecione cliente, obra e data do ensaio.");
      return;
    }
    const quantidade = Math.max(1, Number(form.quantidadeAreas || 1));
    const areasAtuais = Array.isArray(form.areas) ? form.areas : [];
    const areas = Array.from({ length: quantidade }, (_, index) => areasAtuais[index] || criarArea(index));
    const ano = Number(String(form.dataRelatorio || form.dataEnsaio).slice(0, 4)) || new Date().getFullYear();
    const codigo = editando?.id
      ? form.codigo
      : form.codigo || (await gerarSequencial({
          chave: "esclerometrias",
          escopo: ano,
          formato: { padding: 3, ano, template: "CE-RE-ESC-{numero}/{ano}" },
          valorMinimo: obterMaiorSequencial(ensaios, /CE-RE-ESC-(\d+)\/\d{4}/),
        })).codigo;
    const dados = {
      ...form,
      equipamento: {
        ...(form.equipamento || {}),
        indiceNominalBigorna: String(form.equipamento?.indiceNominalBigorna ?? "").trim() === ""
          ? "80"
          : form.equipamento.indiceNominalBigorna,
      },
      codigo,
      quantidadeAreas: quantidade,
      areas,
      clienteDocumento: clienteSelecionado?.documento || form.clienteDocumento || "",
      atualizadoEm: new Date(),
      atualizadoPor: usuario?.uid || null,
    };
    await executarSalvamento(async () => {
      if (editando?.id) {
        await atualizarDocumentoComMesclagem({
          db,
          referencia: doc(db, "tblEsclerometrias", editando.id),
          original: editando,
          atual: { ...editando, ...dados },
          metadados: { atualizadoEm: dados.atualizadoEm, atualizadoPor: dados.atualizadoPor },
        });
      } else {
        await addDoc(collection(db, "tblEsclerometrias"), aplicarTenantAtual({
          ...dados,
          criadoEm: new Date(),
          criadoPor: usuario?.uid || null,
        }));
      }
      setOpen(false);
      await carregarEnsaios();
    });
  }

  const filtrados = useMemo(() => ensaios.filter((item) => {
    if (!filtro.trim()) return true;
    return [item.codigo, item.cliente, item.obra, item.local, item.dataEnsaio]
      .some((valor) => contemTextoPesquisa(normalizar(valor), normalizar(filtro)));
  }), [ensaios, filtro]);
  const inicio = pagina * itensPorPagina;
  const exibidos = filtrados.slice(inicio, inicio + itensPorPagina);

  return <>
    <Navbar />
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <Paper className="cabecalho-lista-cadastro" elevation={0}>
        <Typography variant="h6" fontWeight={700}>Ensaios de Esclerometria</Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>RQ 2.2.17 — Relatórios de esclerometria</Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
          <Button variant="contained" onClick={abrirNovo}>+ Novo ensaio</Button>
          <TextField size="small" label="Pesquisar" value={filtro} onChange={(e) => { setFiltro(e.target.value); setPagina(0); }} sx={{ flex: 1 }} />
          <TextField size="small" label="Qtd. ensaios" value={filtrados.length} InputProps={{ readOnly: true }} sx={{ width: 140 }} />
        </Stack>
      </Paper>
      <Paper sx={{ borderRadius: 2, overflow: "auto", border: "1px solid #e2e8f0", boxShadow: "none" }}>
        <Table className="lista-cadastro-padrao" size="small" sx={{ minWidth: 850 }}>
          <TableHead sx={{ backgroundColor: "#f8fafc" }}><TableRow>
            <TableCell><strong>Código</strong></TableCell><TableCell>Cliente / Obra</TableCell>
            <TableCell>Data do ensaio</TableCell><TableCell>Local</TableCell>
            <TableCell align="center">Áreas</TableCell><TableCell align="center">Ações</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {exibidos.map((item) => <TableRow key={item.id} hover sx={{ cursor: "pointer" }} onClick={() => history.push(`/app/esclerometria/${item.id}`)}>
              <TableCell>{item.codigo || "-"}</TableCell>
              <TableCell><div className="lista-dado-principal">{item.cliente || "-"}</div><div className="lista-dado-secundario">Obra: {item.obra || "-"}</div></TableCell>
              <TableCell>{formatarDataBR(item.dataEnsaio)}</TableCell><TableCell>{item.local || "-"}</TableCell>
              <TableCell align="center">{item.quantidadeAreas || item.areas?.length || 0}</TableCell>
              <TableCell align="center">
                <Tooltip title="Abrir detalhes"><IconButton size="small" onClick={(e) => { e.stopPropagation(); history.push(`/app/esclerometria/${item.id}`); }}><VisibilityIcon color="primary" /></IconButton></Tooltip>
                <Tooltip title="Editar cadastro"><IconButton size="small" onClick={(e) => { e.stopPropagation(); abrirEdicao(item); }}><EditIcon color="primary" /></IconButton></Tooltip>
                <HistoricoCadastro colecao="tblEsclerometrias" documentoId={item.id} registro={item} />
              </TableCell>
            </TableRow>)}
            {loading && <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={28} sx={{ my: 3 }} /></TableCell></TableRow>}
            {!loading && !exibidos.length && <TableRow><TableCell colSpan={6} align="center">Nenhum ensaio cadastrado.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Paper>
      <Stack direction="row" justifyContent="space-between" mt={2}>
        <TextField select size="small" label="Itens" value={itensPorPagina} onChange={(e) => { setItensPorPagina(Number(e.target.value)); setPagina(0); }} sx={{ width: 110 }}>
          {[10, 20, 50].map((valor) => <MenuItem key={valor} value={valor}>{valor}</MenuItem>)}
        </TextField>
        <Stack direction="row" spacing={1}><Button disabled={!pagina} onClick={() => setPagina((p) => p - 1)}>Anterior</Button><Button disabled={inicio + itensPorPagina >= filtrados.length} onClick={() => setPagina((p) => p + 1)}>Próxima</Button></Stack>
      </Stack>
    </Box>

    <Dialog open={open} onClose={() => !salvandoCadastro && setOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>{editando ? "Editar ensaio" : "Novo ensaio de esclerometria"}</DialogTitle>
      <DialogContent dividers><Stack spacing={2} mt={1}>
        <TextField label="Código" value={form.codigo || ""} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="Gerado automaticamente" />
        <Autocomplete options={clientes} getOptionLabel={(opcao) => opcao?.nome || ""} isOptionEqualToValue={(opcao, valor) => opcao.id === valor.id} value={clienteSelecionado}
          onChange={(_, valor) => { setClienteSelecionado(valor); setForm({ ...form, cliente: valor?.nome || "", clienteId: valor?.id || "", clienteDocumento: valor?.documento || "", obra: "", obraId: "", local: "", endereco: "" }); carregarObras(valor?.id); }}
          renderInput={(params) => <TextField {...params} label="Cliente" />} />
        <TextField select label="Empreendimento/Obra" value={form.obraId || ""} disabled={!clienteSelecionado} onChange={(e) => { const obra = obras.find((item) => item.id === e.target.value); setForm({ ...form, obraId: obra?.id || "", obra: obra?.nome || "", endereco: obra?.endereco || "", local: obra?.endereco || obra?.local || "" }); }}>
          {obras.map((obra) => <MenuItem key={obra.id} value={obra.id}>{obra.nome}</MenuItem>)}
        </TextField>
        <TextField label="Local" value={form.local || ""} onChange={(e) => setForm({ ...form, local: e.target.value })} />
        <TextField label="Data do ensaio" type="date" InputLabelProps={{ shrink: true }} value={form.dataEnsaio || ""} onChange={(e) => setForm({ ...form, dataEnsaio: e.target.value })} />
        <TextField label="Data do relatório" type="date" InputLabelProps={{ shrink: true }} value={form.dataRelatorio || ""} onChange={(e) => setForm({ ...form, dataRelatorio: e.target.value })} />
        <TextField label="Quantidade de áreas de ensaio" type="number" inputProps={{ min: 1 }} value={form.quantidadeAreas || 1} onChange={(e) => setForm({ ...form, quantidadeAreas: e.target.value })} />
      </Stack></DialogContent>
      <DialogActions><Button onClick={() => setOpen(false)} disabled={salvandoCadastro}>Cancelar</Button><Button variant="contained" onClick={salvar} disabled={salvandoCadastro}>{salvandoCadastro ? "Salvando..." : "Salvar"}</Button></DialogActions>
    </Dialog>
  </>;
}
